import { LogOut, ChevronDown, Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/hooks/useAuth'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { RoleBadge } from '@/components/admin/RoleBadge'
import { BudgetIndicator } from '@/components/budget'
import { TenantSwitcher } from '@/components/auth'
import { useSidebar } from '@/contexts/SidebarContext'
import { Breadcrumbs } from './Breadcrumbs'

/**
 * Slim top bar replacing the old header.
 * Contains: hamburger (mobile), breadcrumbs, budget indicator, user menu.
 * Height: 48px (h-12).
 */
export function TopBar() {
  const { isAuthenticated, displayName, email, login, logout, isLoading } = useAuth()
  const { data: currentUser } = useCurrentUser()
  const { setMobileOpen } = useSidebar()

  // User initial for avatar
  const initial = displayName ? displayName.charAt(0).toUpperCase() : '?'

  return (
    <header className="sticky top-0 z-30 flex h-12 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 lg:px-6">
      {/* Mobile hamburger */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden h-8 w-8"
        onClick={() => setMobileOpen(true)}
      >
        <Menu className="h-4 w-4" />
        <span className="sr-only">Toggle sidebar</span>
      </Button>

      {/* Breadcrumbs */}
      <div className="flex-1 min-w-0">
        <Breadcrumbs />
      </div>

      {/* Right side: budget + user */}
      <div className="flex items-center gap-3 shrink-0">
        {/* Budget indicator */}
        {isAuthenticated && (
          <BudgetIndicator
            budget={currentUser?.budget}
            effectiveBudgetUsd={
              currentUser?.budget?.remainingUsd !== null
                ? (currentUser?.budget?.usedUsd ?? 0) + (currentUser?.budget?.remainingUsd ?? 0)
                : null
            }
            source={currentUser?.role}
          />
        )}

        {/* User menu */}
        {isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 h-8">
                {/* Avatar circle */}
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                  {initial}
                </span>
                <span className="hidden sm:inline text-sm">{displayName}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{displayName}</p>
                <p className="text-xs text-muted-foreground">{email}</p>
                {currentUser?.role && (
                  <div className="mt-2">
                    <RoleBadge role={currentUser.role} size="sm" />
                  </div>
                )}
              </div>
              <DropdownMenuSeparator />
              {/* Renders nothing unless this person belongs to more than one organisation, which is most
                  installations — see TenantSwitcher. */}
              <TenantSwitcher />
              <DropdownMenuItem onClick={() => logout()}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => login()}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Sign in'}
          </Button>
        )}
      </div>
    </header>
  )
}
